import { fetchCraftedComponents } from './thunks';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_CACHE, getCachedState } from '~store/utils';
import {
  ITemplatedGroup,
  ITemplatedGroupItem,
  IWebformEditorState,
  TAddComponentAction,
  TInspectAction,
  TRemoveComponentAction,
  TToggleInspectorAction,
} from './types';
import { IComponentTemplate, TDatasourceTheme } from '@qodly/shared';
import { WT_FLAGS_KEYS } from './private';

export const FLAGS_INIT_STATE = {
  [WT_FLAGS_KEYS.SIDEBAR_SECTION_RESIZABLE]: true,
};

const stateSlice = createSlice({
  name: 'webforms',
  initialState: (): IWebformEditorState => {
    const { webforms } = getCachedState();

    return {
      i18n: { current: '' },
      ...webforms,
      templates: {
        // ...webforms?.templates,
        data: [],
        selectedGroup: '',
      },
      datasources: {
        ...DEFAULT_CACHE.webforms.datasources,
        ...webforms?.datasources,
      },
      flags: FLAGS_INIT_STATE,
    };
  },
  reducers: {
    toggleInspector: (state, action: TToggleInspectorAction) => {
      const { type } = action.payload;
      state.inspector[type].active = !state.inspector[type].active;

      if (!state.inspector[type].active) {
        state.inspector[type].data = undefined;
      }
    },
    inspect: (state, action: TInspectAction) => {
      const { type, data } = action.payload;

      if (state.inspector[type].active) {
        state.inspector[type].data = data;
      }
    },
    addComponent: (state, action: TAddComponentAction) => {
      state.components.push(action.payload);
    },
    editComponent: (
      state,
      action: PayloadAction<{
        id: string;
        changes: Partial<IComponentTemplate>;
      }>,
    ) => {
      const { id, changes } = action.payload;
      state.components = state.components.map((cmp) =>
        cmp.id === id ? { ...cmp, ...changes } : cmp,
      );
    },
    removeComponent: (state, action: TRemoveComponentAction) => {
      const { payload: id } = action;
      const index = state.components.findIndex(
        (component) => component.id === id,
      );

      if (index !== -1) {
        state.components.splice(index, 1);
      }
    },
    setSelectedGroup: (
      state,
      action: { payload: { selectedGroup: string } },
    ) => {
      state.templates.selectedGroup = action.payload.selectedGroup;
    },
    setTemplatesData: (state, action: { payload: ITemplatedGroup[] }) => {
      state.templates.data = action.payload;
    },
    setTemplateData: (
      state,
      action: { payload: { group: string; components: ITemplatedGroupItem[] } },
    ) => {
      const { group, components } = action.payload;
      const found = state.templates.data.find((t) => t.group === group);
      if (found) found.components = components;
    },
    setDatasourcesTheme: (state, action: { payload: TDatasourceTheme }) => {
      state.datasources.theme = action.payload;
    },
    setWebformsFlag(state, action: PayloadAction<{ [key: string]: any }>) {
      state.flags = {
        ...state.flags,
        ...action.payload,
      };
    },
    setI18nCurrent(state, action: PayloadAction<{ current: string }>) {
      state.i18n = {
        ...state.i18n,
        current: action.payload.current,
      };
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchCraftedComponents.fulfilled, (state, action) => {
      state.components = action.payload;
    });
  },
});

export const {
  toggleInspector,
  inspect,
  addComponent,
  editComponent,
  removeComponent,
  setSelectedGroup,
  setTemplatesData,
  setTemplateData,
  setDatasourcesTheme,
  setWebformsFlag,
  setI18nCurrent,
} = stateSlice.actions;

export default stateSlice;
